import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Injector } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    const expirationDate = new Date(payload.exp * 1000);
    return expirationDate <= new Date();
  } catch {
    return true;
  }
}

@Injectable({
  providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
  private readonly loginRoute = '/auth/login';
  private readonly sessionUserIdentityKey = 'authSessionUserIdentity';
  private readonly companySelectRoute = '/applicationadmin/company-select';
  private readonly applicationSelectRoute =
    '/applicationadmin/application-select';
  private readonly applicationAdminSelectionFlag =
    'applicationAdminCompanySelected';
  private readonly selectedCompanyKey = 'applicationAdminSelectedCompanyId';
  private readonly globalSelectedCompanyKey = 'applicationAdminActiveCompanyId';
  private readonly superAdminSelectionFlag = 'superAdminApplicationSelected';
  private readonly selectedApplicationKey = 'superAdminSelectedApplicationId';
  private readonly globalSelectedApplicationKey =
    'superAdminActiveApplicationId';
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  private readonly exemptAuthRoutes = [
    '/auth/login',
    '/auth/loginjtr',
    '/auth/sso-login',
    '/auth/sso-callback',
    '/auth/forgot-password',
    '/auth/forgot-success',
    '/auth/create-password',
    '/auth/confirm-email',
    '/auth/set-password',
    '/auth/change-email',
    '/auth/verify-otp',
    '/auth/logout',
  ];

  /**
   * Login rotası muafken bile bu endpointler oturum tokenı ister. Örneğin
   * Login başarılı olur olmaz PermissionService UserPermissions çağırır; router
   * henüz /auth/loginjtr'deyken token eklenmezse istek gereksiz 401 alır.
   *
   * Liste bilinçli olarak dar tutulur: şifre/kimlik doğrulama akışlarının
   * mevcut "muaf rotada eski token ekleme" davranışı değişmez.
   */
  private readonly protectedApiOnExemptRoute = [
    '/api/identity/userpermissions',
  ];

  /**
   *
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _authenticationService: AuthService,
    private router: Router,
    private injector: Injector,
  ) {}

  private getStorageUser(): any | null {
    const storageUserRaw = localStorage.getItem('currentUser');
    if (!storageUserRaw) {
      return null;
    }

    try {
      return JSON.parse(storageUserRaw);
    } catch {
      return null;
    }
  }

  private getUserIdentity(user: any): string {
    return String(user?.id || user?.userId || user?.email || '');
  }

  private hasCrossTabUserMismatch(currentUser: any): boolean {
    const currentIdentity = this.getUserIdentity(currentUser);
    if (!currentIdentity) {
      return false;
    }

    const storageUser = this.getStorageUser();
    const storageIdentity = this.getUserIdentity(storageUser);
    const storageToken = String(storageUser?.token || '');
    const currentToken = String(currentUser?.token || '');
    const tabIdentity = sessionStorage.getItem(this.sessionUserIdentityKey);

    if (!tabIdentity) {
      sessionStorage.setItem(this.sessionUserIdentityKey, currentIdentity);
      return false;
    }

    if (tabIdentity !== currentIdentity) {
      if (
        storageIdentity === currentIdentity &&
        storageToken === currentToken
      ) {
        sessionStorage.setItem(this.sessionUserIdentityKey, currentIdentity);
        return false;
      }

      return !!storageUser;
    }

    const inMemoryIdentity = currentIdentity;

    if (!storageUser) {
      return true;
    }

    if (!inMemoryIdentity || !storageIdentity) {
      return false;
    }

    return (
      inMemoryIdentity !== storageIdentity ||
      String(currentUser?.token || '') !== String(storageUser?.token || '')
    );
  }

  private hasApplicationAdminCompanyContextMismatch(currentUser: any): boolean {
    // Sirket katmani ApplicationAdmin/SuperAdmin/SustainabilityTeam arasinda ortaktir
    const usesCompanyContext =
      currentUser?.roles?.includes('ApplicationAdmin') ||
      currentUser?.roles?.includes('SuperAdmin') ||
      currentUser?.roles?.includes('SustainabilityTeam');
    if (!usesCompanyContext) {
      return false;
    }

    const hasCompletedSelection =
      sessionStorage.getItem(this.applicationAdminSelectionFlag) === 'true';
    const tabSelectedCompanyId = sessionStorage.getItem(
      this.selectedCompanyKey,
    );

    if (!hasCompletedSelection || !tabSelectedCompanyId) {
      return false;
    }

    const globalCompanyFromKey = localStorage.getItem(
      this.globalSelectedCompanyKey,
    );

    let globalCompanyFromUser: string | null = null;
    const storageUserRaw = localStorage.getItem('currentUser');
    if (storageUserRaw) {
      try {
        const storageUser = JSON.parse(storageUserRaw);
        globalCompanyFromUser =
          storageUser?.companyId || storageUser?.branchId || null;
      } catch {
        globalCompanyFromUser = null;
      }
    }

    const globalCompanyId = globalCompanyFromKey || globalCompanyFromUser;

    return (
      !!globalCompanyId &&
      String(tabSelectedCompanyId) !== String(globalCompanyId)
    );
  }

  /** SuperAdmin: bu tab'in uygulama secimi global secimden saptiysa true. */
  private hasSuperAdminApplicationContextMismatch(currentUser: any): boolean {
    const isSuperAdmin = currentUser?.roles?.includes('SuperAdmin');
    if (!isSuperAdmin) {
      return false;
    }

    const hasCompletedSelection =
      sessionStorage.getItem(this.superAdminSelectionFlag) === 'true';
    const tabSelectedApplicationId = sessionStorage.getItem(
      this.selectedApplicationKey,
    );

    if (!hasCompletedSelection || !tabSelectedApplicationId) {
      return false;
    }

    const globalApplicationFromKey = localStorage.getItem(
      this.globalSelectedApplicationKey,
    );

    let globalApplicationFromUser: string | null = null;
    const storageUser = this.getStorageUser();
    const storageApplicationId = storageUser?.applicationId;
    if (storageApplicationId && storageApplicationId !== this.emptyGuid) {
      globalApplicationFromUser = storageApplicationId;
    }

    const globalApplicationId =
      globalApplicationFromKey || globalApplicationFromUser;

    return (
      !!globalApplicationId &&
      String(tabSelectedApplicationId) !== String(globalApplicationId)
    );
  }

  private closeOpenOverlays(): void {
    const modalService = this.injector.get(NgbModal);
    if (modalService.hasOpenModals()) {
      modalService.dismissAll();
    }

    if (Swal.isVisible()) {
      Swal.close();
    }
  }

  private isExemptAuthRoute(url: string): boolean {
    const urlWithoutParams = (url || '').split('?')[0];
    return this.exemptAuthRoutes.some((route) =>
      urlWithoutParams.startsWith(route),
    );
  }

  private isCurrentLocationExempt(): boolean {
    const routerUrlExempt = this.isExemptAuthRoute(this.router.url);
    const browserPath = window?.location?.pathname || '';
    const browserUrlExempt = this.isExemptAuthRoute(browserPath);
    return routerUrlExempt || browserUrlExempt;
  }

  private requiresAuthHeaderOnExemptRoute(url: string): boolean {
    const path = (url || '').split('?')[0].toLowerCase();
    return this.protectedApiOnExemptRoute.some((endpoint) =>
      path.includes(endpoint),
    );
  }

  /**
   * Add auth header with jwt if user is logged in and request is to api url
   * @param request
   * @param next
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const currentUser = this._authenticationService.currentUserValue;
    const isLoggedIn = currentUser && currentUser.token;
    const isApiUrl = request.url.startsWith(environment.apiUrl);

    // Check token expiration before making API calls
    if (
      isLoggedIn &&
      isApiUrl &&
      currentUser.token &&
      isTokenExpired(currentUser.token)
    ) {
      this._authenticationService.logout();
      return throwError(() => new Error('Token expired. Please login again.'));
    }

    // Refresh-token mekanizması eklenmeden önce oluşturulmuş oturumlar (JWT var ama
    // refreshToken yok) artık geçersiz sayılır — kullanıcı yeniden login olmalı ki
    // rotating refresh token akışına dahil olsun. Bu, sekmesi açık kalmış eski
    // kullanıcıları da (yeni bir HTTP isteği yaptıklarında) kapsar.
    if (isLoggedIn && isApiUrl && !currentUser.refreshToken) {
      this._authenticationService.logout();
      return throwError(
        () =>
          new Error('Legacy session without refresh token. Please login again.'),
      );
    }
    const isOnCompanySelect = this.router.url.startsWith(
      this.companySelectRoute,
    );
    const isOnApplicationSelect = this.router.url.startsWith(
      this.applicationSelectRoute,
    );
    const isOnLogin = this.router.url.startsWith(this.loginRoute);
    const isOnExemptAuthRoute = this.isCurrentLocationExempt();

    if (
      isLoggedIn &&
      isApiUrl &&
      !isOnLogin &&
      !isOnExemptAuthRoute &&
      this.hasCrossTabUserMismatch(currentUser)
    ) {
      this.closeOpenOverlays();
      sessionStorage.removeItem(this.applicationAdminSelectionFlag);
      sessionStorage.removeItem(this.selectedCompanyKey);
      sessionStorage.removeItem(this.superAdminSelectionFlag);
      sessionStorage.removeItem(this.selectedApplicationKey);
      sessionStorage.removeItem(this.sessionUserIdentityKey);
      this._authenticationService.currentUserValue = undefined;
      this.router.navigate([this.loginRoute], {
        queryParams: { reason: 'session-changed' },
      });

      return throwError(
        () => new Error('Authentication context changed in another tab.'),
      );
    }

    // Uygulama baglami sirket baglamindan once kontrol edilir: uygulama
    // degistiyse sirket secimi de zaten gecersizdir.
    if (
      isLoggedIn &&
      isApiUrl &&
      !isOnApplicationSelect &&
      !isOnExemptAuthRoute &&
      this.hasSuperAdminApplicationContextMismatch(currentUser)
    ) {
      sessionStorage.removeItem(this.superAdminSelectionFlag);
      sessionStorage.removeItem(this.applicationAdminSelectionFlag);
      this.router.navigate([this.applicationSelectRoute], {
        queryParams: { reason: 'application-context-changed' },
      });

      return throwError(
        () =>
          new Error('SuperAdmin application context changed in another tab.'),
      );
    }

    if (
      isLoggedIn &&
      isApiUrl &&
      !isOnCompanySelect &&
      !isOnApplicationSelect &&
      !isOnExemptAuthRoute &&
      this.hasApplicationAdminCompanyContextMismatch(currentUser)
    ) {
      sessionStorage.removeItem(this.applicationAdminSelectionFlag);
      this.router.navigate([this.companySelectRoute], {
        queryParams: { reason: 'company-context-changed' },
      });

      return throwError(
        () =>
          new Error('ApplicationAdmin company context changed in another tab.'),
      );
    }

    // Uygulama dili giris yapilmamis isteklerde de (ForgotPassword vb.) gitmeli;
    // yoksa backend tarayicinin kendi Accept-Language'ini gorur ve ör. sifre
    // sifirlama maili ekranda TR secili olsa bile Ingilizce gider.
    if (isApiUrl) {
      let appLang = localStorage.getItem('language');
      request = request.clone({
        headers: request.headers.set(
          'Accept-Language',
          appLang == 'tr' ? 'tr-TR' : 'en-US',
        ),
      });
    }

    if (isLoggedIn && isApiUrl) {
      if (!request.headers.has('Accept')) {
        request = request.clone({
          headers: request.headers.set('Accept', 'application/json'),
        });
      }

      // confirm-email/forgot-password/set-password/create-password vb. muaf
      // rotalar BASKA bir kimlige (userId+code ile) islem yapar; tarayicida
      // baska bir hesap acikken bu istegin ustune o hesabin token'i binmemeli
      // — bkz. isOnExemptAuthRoute tanimindaki exemptAuthRoutes listesi.
      if (
        !isOnExemptAuthRoute ||
        this.requiresAuthHeaderOnExemptRoute(request.url)
      ) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${currentUser.token}`,
          },
        });
      }
    }

    // Remove CORS headers from client-side - these should be set by the server
    // let headers = request.headers;
    // headers.append('Cache-Control', 'no - cache');
    // headers.append('Pragma', 'no - cache');
    // headers.append("Access-Control-Allow-Origin", "*");
    // headers.append("Access-Control-Allow-Credentials", "true");
    // headers.append("Access-Control-Allow-Methods", "GET,HEAD,OPTIONS,POST,PUT");
    // headers.append("Access-Control-Allow-Headers", "Access-Control-Allow-Headers, Origin,Accept, X-Requested-With, Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers");

    return next.handle(request);
  }
}
